"use client";

import type { BlokProps } from "@trydig/storyblok";
import { useQueryState } from "nuqs";
import type { ISbStoryData } from "storyblok";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { renderRichText } from "@/components/bloks/utils";
import { Accordion } from "@/components/ui/Accordion";
import { Prose } from "@/components/ui/Prose";
import { Typography } from "@/components/ui/Typography";
import { FAQ_TOPIC_PARAM } from "@/features/faq/config";
import type { FaqDetailsPageStoryblok, FaqGroupStoryblok } from "@/generated/sb/sb-types";

export const FaqGroupBlok = ({ blok }: BlokProps<FaqGroupStoryblok>) => {
	const [activeSlug] = useQueryState(FAQ_TOPIC_PARAM);

	if (activeSlug && activeSlug !== blok.title) return null;

	// Storyblok hands us string UUIDs instead of story objects when a referenced
	// story isn't published or `resolve_relations` didn't cover this field —
	// filter those out so `.id` / `.content` access below doesn't crash.
	const items = (blok.items ?? []).filter(
		(item): item is ISbStoryData<FaqDetailsPageStoryblok> =>
			typeof item === "object" && item !== null,
	);

	return (
		<BlokWrap editable={blok} as="div" className="flex flex-col gap-grid-section-vertical-md">
			<Typography variant="heading-display-xs" as="h2" className="text-text-dark-text-1">
				{blok.title}
			</Typography>
			{!!items.length && (
				<Accordion
					items={items.map((item) => ({
						id: item.id.toString(),
						title: item.content?.question || "",
						content: <Prose>{renderRichText(item.content?.answer)}</Prose>,
					}))}
					size="large"
				/>
			)}
		</BlokWrap>
	);
};
